/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { useRef, useImperativeHandle, forwardRef } from 'react'
import { v4 as uuidV4 } from 'uuid'

import styles from './Upload.less'

import {
    getFileType,
    getFileRect,
    formatSize,
    readFile,
    getVideoMetaData,
    adjustImg,
    makeThumb,
    getSuffix,
    isImage,
    isVideo,
    transBlobToFile,
    toast
} from './utils'
import { traverseFileTree, attrAccept } from './traverseFileTree'

import ajax from './ajax'

const toastMessageEnum = {
    invalidType: '无效格式',
    invalidSize: '无效大小',
    invalidRect: '无效尺寸',
    invalidRadio: '无效比例',
    invalidDuration: '无效时长'
}

const Upload = forwardRef(
    (
        {
            value,
            onChange,
            name,
            directory,
            fileDirectory,
            isTranscoder,
            multiple,
            format,
            accept,
            maxNum,
            maxSize,
            minSize,
            maxWidth,
            minWidth,
            maxHeight,
            minHeight,
            radio,
            minDuration,
            maxDuration,
            syncPreview,
            autoPreview,
            autoUpload,
            thumbnail,
            disabled,
            enableToast,
            singleErrorToast, // 单个报错自定义
            audit,
            shareSignature
        },
        ref
    ) => {
        const $this = useRef({
            value: []
        })
        const $input = useRef(null)

        $this.current.value = value || []

        // 检查格式
        const checkType = file => {
            const type = getFileType(file)

            if (format.length) {
                return format.includes(type)
            }

            return !(accept !== '*' && !type.startsWith(accept.split('/')[0]))
        }

        // 检查大小
        const checkSize = file => {
            if (typeof minSize === 'number') {
                if (file.size < minSize * 1024 * 1024) {
                    return false
                }
            }

            if (typeof maxSize === 'number') {
                if (file.size > maxSize * 1024 * 1024) {
                    return false
                }
            }

            return true
        }

        // 检查尺寸
        const checkRect = async file => {
            if (
                typeof minWidth === 'undefined' &&
                typeof maxWidth === 'undefined' &&
                typeof minHeight === 'undefined' &&
                typeof maxHeight === 'undefined'
            ) {
                return Promise.resolve()
            }

            try {
                const { naturalWidth, naturalHeight } = await getFileRect(file)

                if (
                    (minWidth && naturalWidth < minWidth) ||
                    (maxWidth && naturalWidth > maxWidth) ||
                    (minHeight && naturalHeight < minHeight) ||
                    (maxHeight && naturalHeight > maxHeight)
                ) {
                    return Promise.reject(new Error(toastMessageEnum.invalidRect))
                }
            } catch (e) {
                console.error('catchError', e)
                return Promise.reject(e)
            }
            return Promise.resolve()
        }

        // 检查比例
        const checkRadio = async file => {
            if (typeof radio === 'undefined') {
                return Promise.resolve()
            }

            // 提前视频校验 （火狐会有问题）
            if (isVideo(file)) {
                try {
                    const { width, height } = await getVideoMetaData(file)

                    const curRadio = width / height

                    const calRadio = item => {
                        const arr = item.split(':').map(v => Number(v))

                        return arr[0] / arr[1]
                    }

                    // 数组
                    if (Array.isArray(radio)) {
                        if (!radio.some(v => calRadio(v) === curRadio)) {
                            return Promise.reject(new Error(toastMessageEnum.invalidRadio))
                        }

                        return Promise.resolve()
                    }

                    // 字符串，如：3:4
                    if (typeof radio === 'string') {
                        if (calRadio(radio) !== curRadio) {
                            return Promise.reject(new Error(toastMessageEnum.invalidRadio))
                        }
                    }
                } catch (e) {
                    console.error('catchError', e)
                    return Promise.reject(e)
                }
            }

            return Promise.resolve()
        }

        // 检查时长
        const checkDuration = async file => {
            if (typeof minDuration === 'undefined' && typeof maxDuration === 'undefined') {
                return Promise.resolve()
            }

            // 提前视频校验 （火狐会有问题）
            if (isVideo(file)) {
                const { duration } = await getVideoMetaData(file)

                if ((minDuration && duration < minDuration) || (maxDuration && duration > maxDuration)) {
                    return Promise.reject(new Error(toastMessageEnum.invalidDuration))
                }
            }

            return Promise.resolve()
        }

        const humanCheckMessage = (msg, file) => {
            const getLabel = () => {
                if (isVideo(file)) {
                    return '视频'
                }

                if (isImage(file)) {
                    return '图片'
                }

                return ''
            }

            const label = getLabel()

            // if (msg === toastMessageEnum.invalidType) {
            //     return msg
            // }

            // if (msg === toastMessageEnum.invalidSize) {
            //     return msg
            // }

            // if (msg === toastMessageEnum.invalidRect) {
            //     return msg
            // }

            if (msg === toastMessageEnum.invalidRadio) {
                const arr = typeof radio === 'string' ? [radio] : radio

                return `${label}${`宽高比需为${arr.join('或')}`}`
            }

            if (msg === toastMessageEnum.invalidDuration) {
                return `${label}${`时长需${[
                    minDuration || `不少于${minDuration}s`,
                    maxDuration || `不超过${maxDuration}s`
                ]
                    .filter(Boolean)
                    .join('且')}`}`
            }

            return msg
        }

        // 格式化文件
        const formatFile = args => {
            const { width, height } = thumbnail

            const uid = uuidV4()

            return {
                uid,
                time_stamp: Date.now(),
                thumbnail: {
                    width,
                    height
                },
                ...(args || {})
            }
        }

        // 预览文件
        const previewFile = async ({ file }) => {
            if (!FileReader) {
                return file
            }

            // 过滤gif类型图片，暂时去掉。为了最后生成图片的名称格式统一
            // if (WHITE_PREVIEW_TYPE.includes(file.type)) {
            //     return file
            // }

            try {
                const { source } = file

                const { img, orientation } = await readFile(source)

                // hack: 文件类型和后缀不一致的问题
                const type = orientation === 1 ? 'image/png' : file.type

                let adImg = img

                // 不是1再处理
                if (orientation !== 1) {
                    adImg = await adjustImg(img, {
                        orientation,
                        type
                    })
                }

                const thumbImg = await makeThumb(img, {
                    ...thumbnail,
                    type
                })

                return {
                    ...file,
                    status: 'previewed',
                    source: orientation === 1 ? source : transBlobToFile(adImg.blob, file.name, type), // hack: 文件大小
                    thumbnailUrl: thumbImg.url,
                    width: adImg?.width || 0,
                    height: adImg?.height || 0
                }
            } catch (e) {
                console.error('catchError', e)
            }

            return file
        }

        const updateValue = (file, payload) => {
            if (multiple) {
                $this.current.value = $this.current.value.map(v => {
                    if (v.uid === file.uid) {
                        return {
                            ...file,
                            ...payload
                        }
                    }

                    return v
                })

                onChange([...$this.current.value])
            } else {
                const newValue = {
                    ...file,
                    ...payload
                }

                onChange(newValue)
            }
        }

        // 进度
        const uploadProgress = ({ e, file }) => {
            updateValue(file, {
                status: 'uploading',
                percent: e.percent || 0
            })
        }

        // 成功
        const uploadSuccess = ({ e, file }) => {
            updateValue(file, {
                status: 'done',
                url: e.url,
                thumbnailUrl: file.thumbnailUrl ? file.thumbnailUrl : e.thumbnailUrl
            })
        }

        // 错误
        const uploadError = ({ file }) => {
            updateValue(file, {
                status: 'error',
                message: '上传失败'
            })
        }

        // 上传文件
        const uploadFile = async ({ file }) => {
            const options = {
                name,
                fileDirectory,
                isTranscoder,
                file,
                audit,
                shareSignature,
                onProgress: e => uploadProgress({ e, file }),
                onSuccess: e => uploadSuccess({ e, file }),
                onError: e => uploadError({ e, file })
            }

            try {
                file.uploadTask = await ajax(options)
            } catch (e) {
                console.error('catchError', e)
                uploadError({ e, file })
            }
        }

        const resolveMultiple = async files => {
            let curFiles = Array.from(files)

            const totalNum = curFiles.length

            const lastIndex =
                typeof maxNum === 'number' && maxNum - $this.current.value.length < totalNum
                    ? maxNum - $this.current.value.length
                    : totalNum

            curFiles = curFiles.slice(0, lastIndex)

            if (curFiles.length <= 0) return

            const tempFiles = []

            for (let file of curFiles) {
                // 包装文件

                const formattedFile = formatFile({
                    status: 'initial',
                    source: file,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    width: file.width,
                    height: file.height,
                    ext: getSuffix(file.name),
                    sizeFormat: formatSize(file.size),
                    percent: 0
                })

                let fileChecked = false

                // 检查格式
                if (!fileChecked && !checkType(file)) {
                    Object.assign(formattedFile, {
                        status: 'error',
                        message: toastMessageEnum.invalidType
                    })

                    fileChecked = true
                }

                // 检查大小
                if (!fileChecked && !checkSize(file)) {
                    Object.assign(formattedFile, {
                        status: 'error',
                        message: toastMessageEnum.invalidSize
                    })

                    fileChecked = true
                }

                if (!fileChecked) {
                    // 检查尺寸
                    try {
                        await checkRect(file)
                    } catch (e) {
                        console.error('catchError', e)
                        Object.assign(formattedFile, {
                            status: 'error',
                            message: toastMessageEnum.invalidRect
                        })

                        fileChecked = true
                    }
                }

                // if (!fileChecked) {
                //     // 检查比例
                //     try {
                //         await checkRadio(file)
                //     } catch (e) {
                //         console.error('catchError', e)
                //         Object.assign(formattedFile, {
                //             status: 'error',
                //             message: toastMessageEnum.invalidRadio
                //         })

                //         fileChecked = true
                //     }
                // }

                // if (!fileChecked) {
                //     // 检查时长
                //     try {
                //         await checkDuration(file)
                //     } catch (e) {
                //         console.error('catchError', e)
                //         Object.assign(formattedFile, {
                //             status: 'error',
                //             message: toastMessageEnum.invalidDuration
                //         })

                //         fileChecked = true
                //     }
                // }

                tempFiles.push(formattedFile)
                $this.current.value = $this.current.value.concat(formattedFile)
            }

            onChange([...$this.current.value])

            const successFn = data => {
                $this.current.value = $this.current.value.map(v => {
                    if (v.uid === data.uid) {
                        return data
                    }

                    return v
                })

                onChange([...$this.current.value])

                autoUpload &&
                    uploadFile({
                        file: data
                    })
            }

            const normalFiles = tempFiles.filter(v => v.status !== 'error')

            for (let file of normalFiles) {
                if (isImage(file) && autoPreview) {
                    if (syncPreview) {
                        const data = await previewFile({ file })

                        successFn(data)
                    } else {
                        previewFile({ file }).then(successFn)
                    }
                } else {
                    autoUpload &&
                        uploadFile({
                            file
                        })
                }
            }
        }

        const resolveSingle = async file => {
            if (!file) {
                return
            }

            // 检查格式
            if (!checkType(file)) {
                if (singleErrorToast && singleErrorToast(toastMessageEnum.invalidType)) {
                    return
                }

                // TODO 完善toast文案，显示具体要求
                enableToast && toast.error(humanCheckMessage(toastMessageEnum.invalidType, file))
                return
            }

            // 检查大小
            if (!checkSize(file)) {
                if (singleErrorToast && singleErrorToast(toastMessageEnum.invalidSize)) {
                    return
                }

                // TODO 完善toast文案，显示具体要求
                enableToast && toast.error(humanCheckMessage(toastMessageEnum.invalidSize, file))
                return
            }

            // 检查尺寸
            try {
                await checkRect(file)
            } catch (e) {
                console.error('catchError', e)
                if (singleErrorToast && singleErrorToast(toastMessageEnum.invalidRect)) {
                    return
                }

                // TODO 完善toast文案，显示具体要求
                enableToast && toast.error(humanCheckMessage(toastMessageEnum.invalidRect, file))
                return
            }

            // // 检查比例（暂只支持视频）
            // try {
            //     await checkRadio(file)
            // } catch (e) {
            //     console.error('catchError', e)

            //     if (singleErrorToast && singleErrorToast(toastMessageEnum.invalidRadio)) {
            //         return
            //     }

            //     enableToast && toast.error(humanCheckMessage(toastMessageEnum.invalidRadio, file))
            //     return
            // }

            // // 检查时长（暂只支持视频）
            // try {
            //     await checkDuration(file)
            // } catch (e) {
            //     console.error('catchError', e)

            //     if (singleErrorToast && singleErrorToast(toastMessageEnum.invalidDuration)) {
            //         return
            //     }

            //     enableToast && toast.error(humanCheckMessage(toastMessageEnum.invalidDuration, file))
            //     return
            // }

            // 包装文件
            const formattedFile = formatFile({
                status: 'initial',
                source: file,
                name: file.name,
                type: file.type,
                size: file.size,
                ext: getSuffix(file.name),
                sizeFormat: formatSize(file.size),
                percent: 0
            })

            onChange(formattedFile)

            const successFn = data => {
                onChange(data)

                autoUpload &&
                    uploadFile({
                        file: data
                    })
            }

            if (isImage(formattedFile) && autoPreview) {
                if (syncPreview) {
                    try {
                        const data = await previewFile({ file: formattedFile })

                        successFn(data)
                    } catch (e) {
                        console.error('catchError', e)
                    }
                } else {
                    previewFile({ file: formattedFile }).then(successFn)
                }
            } else {
                autoUpload && uploadFile({ file: formattedFile })
            }
        }

        // 文件处理
        const processFile = files => {
            if (multiple) {
                resolveMultiple(files)
            } else {
                resolveSingle(files[0])
            }
        }

        // 拖拽文件
        const dropFile = e => {
            e.preventDefault()

            if (e.type === 'dragover') {
                return
            }

            if (directory) {
                traverseFileTree(
                    Array.from(e.dataTransfer.items),
                    files => {
                        processFile(files)
                    },
                    _file => attrAccept(_file, accept)
                )
            } else {
                processFile(e.dataTransfer.files)
            }
        }

        // 剪贴板文件
        const readClipboardFile = async () => {
            const clipboardItems = await navigator.clipboard.read()

            const clipboardFiles = []

            for (const clipboardItem of clipboardItems) {
                const types = clipboardItem.types

                const imageTypes = types.filter(v => v.startsWith('image/'))

                for (const type of imageTypes) {
                    const blob = await clipboardItem.getType(type)

                    const suffiex = type.split('/')?.[1] || 'png'
                    const uid = uuidV4().replace(/-/g, '').toUpperCase()

                    if (!blob.name) {
                        blob.name = `${uid}.${suffiex}`
                    }

                    console.log('文件:', blob)

                    clipboardFiles.push(blob)
                }
            }

            if (!clipboardFiles?.length) {
                toast.warn('当前剪贴板无信息，请先截图或复制后再操作')
                return
            }

            if (multiple) {
                processFile(clipboardFiles)
            } else {
                processFile([clipboardFiles[clipboardFiles.length - 1]])
            }
        }

        const clear = () => {
            $this.current.value = []
        }

        useImperativeHandle(ref, () => ({
            select: () => $input.current.click(),
            drop: dropFile,
            readClipboardFile,
            clear
        }))

        // 选择文件
        const handleChange = e => {
            e.persist()
            const files = e.target.files || e.dataTransfer.files

            if (!files) {
                return
            }

            processFile(files)

            $input.current.value = null
        }

        return (
            <input
                className={styles.container}
                ref={$input}
                type="file"
                accept={accept}
                disabled={disabled}
                multiple={multiple}
                onClick={e => e.stopPropagation()}
                onChange={handleChange}
            />
        )
    }
)

Upload.defaultProps = {
    name: 'file',
    directory: true, // 是否支持文件夹上传
    fileDirectory: undefined, // 自定义传到oss的目录
    isTranscoder: false, // 视频上传是否进行转码
    multiple: true, // 是否多选
    format: [], // 格式校验
    accept: 'image/*', // 文件类型
    maxNum: undefined, // 最大数量
    minNum: 0,
    maxSize: undefined, // 最大尺寸（单位：M）
    minSize: 0, // 最小尺寸（单位：M）
    maxWidth: undefined,
    minWidth: undefined,
    maxHeight: undefined,
    minHeight: undefined,
    radio: undefined,
    minDuration: undefined,
    maxDuration: undefined,
    syncPreview: false, // 是否同步处理
    autoPreview: true, // 是否自动预览
    autoUpload: true, // 是否自动上传
    enableToast: false, // 是否提示文件检查错误（仅单选模式有效，多选模式静默过滤不合法文件）
    thumbnail: {
        // TODO 缩略图配置项，暂时没有进行合并处理，外部需要传入完整配置
        width: 84,
        height: 84,
        quality: 70,
        allowMagnify: true,
        crop: true,
        type: 'image/png'
    }
}

export default Upload
